import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { formatCurrency, formatDate } from '../utils/helpers'

export default function PublicContractPage() {
  const { token } = useParams()
  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [form, setForm] = useState({ signer_name: '', signer_email: '', signer_title: '' })
  const [signed, setSigned] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const canvasRef = useRef(null)
  const [drawing, setDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  useEffect(() => {
    fetch(`/api/public/contract/${token}`)
      .then(r => { if (!r.ok) throw new Error('Not found'); return r.json() })
      .then(setContract)
      .catch(() => setError('This contract link is invalid or has expired.'))
      .finally(() => setLoading(false))
  }, [token])

  // Signature pad
  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    const touch = e.touches ? e.touches[0] : e
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top }
  }
  const startDraw = (e) => { setDrawing(true); const ctx = canvasRef.current.getContext('2d'); ctx.beginPath(); const { x, y } = getXY(e); ctx.moveTo(x, y) }
  const draw = (e) => {
    if (!drawing) return; e.preventDefault()
    const ctx = canvasRef.current.getContext('2d'); ctx.strokeStyle = '#1a1a2e'; ctx.lineWidth = 2; ctx.lineCap = 'round'
    const { x, y } = getXY(e); ctx.lineTo(x, y); ctx.stroke(); setHasSignature(true)
  }
  const endDraw = () => setDrawing(false)
  const clearSig = () => { const ctx = canvasRef.current.getContext('2d'); ctx.clearRect(0, 0, 400, 150); setHasSignature(false) }

  const submit = async () => {
    if (!form.signer_name || !hasSignature) return alert('Name and signature required')
    setSubmitting(true)
    try {
      const signature_data = canvasRef.current.toDataURL()
      const r = await fetch(`/api/public/contract/${token}/sign`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, signature_data })
      })
      if (!r.ok) throw new Error('Failed')
      setSigned(true)
    } catch { alert('Error submitting signature') } finally { setSubmitting(false) }
  }

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><div className="spinner-border text-primary" /></div>
  if (error) return <div style={{ maxWidth: 500, margin: '4rem auto', textAlign: 'center' }}><h2>Contract Not Found</h2><p className="text-secondary">{error}</p></div>

  const c = contract
  const alreadySigned = c.status === 'signed'

  return (
    <div style={{ maxWidth: 800, margin: '2rem auto', padding: '0 1rem' }}>
      <div className="card">
        <div className="card-header" style={{ background: '#1a1a2e', color: '#fff' }}>
          <div>
            <h2 className="card-title mb-0" style={{ color: '#fff' }}>{c.hotel_name}</h2>
            <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>{c.hotel_city}, {c.hotel_state} — Contract</div>
          </div>
        </div>
        <div className="card-body">
          <h3 className="mb-3">{c.title}</h3>
          <div className="row mb-4" style={{ fontSize: '0.9rem' }}>
            <div className="col-sm-6"><strong>Client:</strong> {c.contact_name || c.company}</div>
            <div className="col-sm-6"><strong>Amount:</strong> <span className="text-primary fw-bold">{formatCurrency(c.amount)}</span></div>
          </div>
          {c.content && <div className="mb-4" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, border: '1px solid var(--tblr-border-color)', borderRadius: 4, padding: '1rem', maxHeight: 400, overflow: 'auto' }}>{c.content}</div>}

          {signed ? (
            <div className="alert alert-success">Contract signed successfully. A confirmation will be sent to your email. Thank you!</div>
          ) : alreadySigned ? (
            <div className="alert alert-info">This contract was signed on {formatDate(c.signed_date)} by {c.signer_name}.</div>
          ) : (
            <div className="card" style={{ background: 'var(--tblr-bg-surface-secondary)' }}>
              <div className="card-body">
                <h4 className="mb-3">Sign Below</h4>
                <div className="row g-3 mb-3">
                  <div className="col-md-4"><label className="form-label">Full Name *</label><input className="form-control" value={form.signer_name} onChange={e => setForm({ ...form, signer_name: e.target.value })} /></div>
                  <div className="col-md-4"><label className="form-label">Email</label><input type="email" className="form-control" value={form.signer_email} onChange={e => setForm({ ...form, signer_email: e.target.value })} /></div>
                  <div className="col-md-4"><label className="form-label">Title</label><input className="form-control" value={form.signer_title} onChange={e => setForm({ ...form, signer_title: e.target.value })} /></div>
                </div>
                <label className="form-label">Signature *</label>
                <div style={{ border: '2px solid #206bc4', borderRadius: 4, background: '#fff', display: 'inline-block', cursor: 'crosshair' }}>
                  <canvas ref={canvasRef} width={400} height={150}
                    onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                    onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw} />
                </div>
                <div className="mt-1 mb-3"><button className="btn btn-sm btn-outline-secondary" onClick={clearSig}>Clear Signature</button></div>
                <button className="btn btn-primary btn-lg" disabled={submitting} onClick={submit}>
                  {submitting ? 'Signing...' : 'Sign Contract'}
                </button>
                <p className="text-secondary mt-2" style={{ fontSize: '0.75rem' }}>
                  By signing, you agree to the terms outlined in this contract. Your signature and IP address will be recorded.
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="card-footer text-center text-secondary" style={{ fontSize: '0.8rem' }}>Powered by Leadiox Hotel Sales CRM</div>
      </div>
    </div>
  )
}
